import { useState } from 'react';
import {
  Plus,
  Search,
  Filter,
  Megaphone,
  Play,
  Pause,
  X,
} from 'lucide-react';
import {
  useCampaigns,
  useCreateCampaign,
  useUpdateCampaign,
  useLaunchCampaign,
  usePauseCampaign,
  useCampaignStats,
  type CampaignFilters,
  type Campaign,
  type CampaignStatus,
  type CampaignType,
} from '../hooks/useCampaigns';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface CampaignsViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const statusLabels: Record<CampaignStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'gray' },
  scheduled: { label: 'Scheduled', color: 'blue' },
  active: { label: 'Active', color: 'green' },
  paused: { label: 'Paused', color: 'orange' },
  completed: { label: 'Completed', color: 'purple' },
  cancelled: { label: 'Cancelled', color: 'red' },
};

const typeLabels: Record<CampaignType, { label: string; color: string }> = {
  email: { label: 'Email', color: 'blue' },
  drip: { label: 'Drip', color: 'indigo' },
  event: { label: 'Event', color: 'purple' },
  referral: { label: 'Referral', color: 'green' },
  social: { label: 'Social', color: 'pink' },
};

export function CampaignsView({ onNavigate }: CampaignsViewProps) {
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<CampaignType | ''>('');
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [formData, setFormData] = useState({ name: '', type: 'email' as CampaignType, subject: '' });

  const filters: CampaignFilters = {
    status: statusFilter || undefined,
    type: typeFilter || undefined,
    search: searchText || undefined,
    limit: 100,
  };

  const { data, isLoading, error } = useCampaigns(filters);
  const { data: detailStats } = useCampaignStats(selectedCampaign?.id ?? null);
  const createCampaign = useCreateCampaign();
  const launchCampaign = useLaunchCampaign();
  const pauseCampaign = usePauseCampaign();

  const campaigns = data?.campaigns ?? [];

  function handleCreate() {
    createCampaign.mutate(
      { name: formData.name, type: formData.type, subject: formData.subject || undefined },
      {
        onSuccess: () => {
          setShowForm(false);
          setFormData({ name: '', type: 'email', subject: '' });
        },
      },
    );
  }

  function renderStatBar(label: string, value: number, total: number, color: string) {
    const pct = total > 0 ? (value / total) * 100 : 0;
    return (
      <div className="mb-2">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-slate-600">{label}</span>
          <span className="font-medium text-slate-900">{value} ({Math.round(pct)}%)</span>
        </div>
        <div className="h-2 w-full rounded-full bg-surface-200">
          <div className={cn('h-2 rounded-full', color)} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Campaigns</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} campaign${data.total !== 1 ? 's' : ''}` : 'Marketing campaigns'}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4" />
          New Campaign
        </button>
      </div>

      {/* Filter Bar */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          <form onSubmit={(e) => e.preventDefault()} className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search campaigns..."
              className="input pl-9"
            />
          </form>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn('btn-secondary btn-sm', showFilters && 'bg-brand-50 border-brand-200 text-brand-700')}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-3 flex items-center gap-3">
            <div>
              <label className="label mb-1">Status</label>
              <select className="input py-1.5" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as CampaignStatus | '')}>
                <option value="">All</option>
                {Object.entries(statusLabels).map(([val, cfg]) => (
                  <option key={val} value={val}>{cfg.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label mb-1">Type</label>
              <select className="input py-1.5" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as CampaignType | '')}>
                <option value="">All</option>
                {Object.entries(typeLabels).map(([val, cfg]) => (
                  <option key={val} value={val}>{cfg.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="flex">
        {/* Table */}
        <div className={cn('flex-1 p-6', selectedCampaign && 'pr-0')}>
          {error ? (
            <div className="card p-8 text-center text-sm text-red-600">Failed to load campaigns.</div>
          ) : isLoading ? (
            <div className="card">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 border-b border-surface-100 px-4 py-3 last:border-0">
                  <div className="h-4 w-48 animate-pulse rounded bg-surface-200" />
                  <div className="flex-1" />
                  <div className="h-4 w-20 animate-pulse rounded bg-surface-200" />
                </div>
              ))}
            </div>
          ) : campaigns.length > 0 ? (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-200 bg-surface-50">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Name</th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Type</th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Sent</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Opened</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Clicked</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Created</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-100">
                  {campaigns.map((c) => {
                    const sCfg = statusLabels[c.status];
                    const tCfg = typeLabels[c.type];
                    return (
                      <tr key={c.id} className={cn('hover:bg-surface-50 cursor-pointer', selectedCampaign?.id === c.id && 'bg-brand-50')} onClick={() => setSelectedCampaign(c)}>
                        <td className="px-4 py-3 text-sm font-medium text-slate-900">{c.name}</td>
                        <td className="px-4 py-3 text-center"><Badge color={tCfg.color} size="sm">{tCfg.label}</Badge></td>
                        <td className="px-4 py-3 text-center"><Badge color={sCfg.color} size="sm">{sCfg.label}</Badge></td>
                        <td className="px-4 py-3 text-sm text-right text-slate-600">{c.stats.sent}</td>
                        <td className="px-4 py-3 text-sm text-right text-slate-600">{c.stats.opened}</td>
                        <td className="px-4 py-3 text-sm text-right text-slate-600">{c.stats.clicked}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{new Date(c.createdAt).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            {(c.status === 'draft' || c.status === 'scheduled' || c.status === 'paused') && (
                              <button className="btn-secondary btn-sm" onClick={() => launchCampaign.mutate(c.id)} disabled={launchCampaign.isPending} title="Launch">
                                <Play className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {c.status === 'active' && (
                              <button className="btn-secondary btn-sm" onClick={() => pauseCampaign.mutate(c.id)} disabled={pauseCampaign.isPending} title="Pause">
                                <Pause className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon={Megaphone}
              title="No campaigns yet"
              description="Create a campaign to reach your audience."
              actionLabel="New Campaign"
              onAction={() => setShowForm(true)}
            />
          )}
        </div>

        {/* Detail Panel */}
        {selectedCampaign && (
          <div className="w-96 border-l border-surface-200 bg-white p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">{selectedCampaign.name}</h2>
              <button onClick={() => setSelectedCampaign(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Badge color={typeLabels[selectedCampaign.type].color}>{typeLabels[selectedCampaign.type].label}</Badge>
                <Badge color={statusLabels[selectedCampaign.status].color}>{statusLabels[selectedCampaign.status].label}</Badge>
              </div>

              {/* Stats Dashboard */}
              <div>
                <p className="text-xs font-medium text-slate-500 mb-3">Performance</p>
                {(() => {
                  const stats = detailStats ?? selectedCampaign.stats;
                  const total = stats.sent || 1;
                  return (
                    <div>
                      {renderStatBar('Delivered', stats.delivered, total, 'bg-blue-500')}
                      {renderStatBar('Opened', stats.opened, total, 'bg-green-500')}
                      {renderStatBar('Clicked', stats.clicked, total, 'bg-purple-500')}
                      {renderStatBar('Bounced', stats.bounced, total, 'bg-red-500')}
                    </div>
                  );
                })()}
              </div>

              <div className="pt-4 border-t border-surface-200 flex gap-2">
                {(selectedCampaign.status === 'draft' || selectedCampaign.status === 'paused') && (
                  <button className="btn-primary btn-sm flex-1" onClick={() => launchCampaign.mutate(selectedCampaign.id)}>
                    <Play className="h-3.5 w-3.5" /> Launch
                  </button>
                )}
                {selectedCampaign.status === 'active' && (
                  <button className="btn-secondary btn-sm flex-1" onClick={() => pauseCampaign.mutate(selectedCampaign.id)}>
                    <Pause className="h-3.5 w-3.5" /> Pause
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Campaign Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">New Campaign</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label mb-1">Name</label>
                <input className="input" value={formData.name} onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="label mb-1">Type</label>
                <select className="input" value={formData.type} onChange={(e) => setFormData((f) => ({ ...f, type: e.target.value as CampaignType }))}>
                  {Object.entries(typeLabels).map(([val, cfg]) => (
                    <option key={val} value={val}>{cfg.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label mb-1">Subject</label>
                <input className="input" value={formData.subject} onChange={(e) => setFormData((f) => ({ ...f, subject: e.target.value }))} />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleCreate} disabled={createCampaign.isPending || !formData.name}>
                {createCampaign.isPending ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
